import { Button, Col, Flex, Form, Modal, Row, Spin, Tag, Typography } from 'antd'
import { RetargetingListBean } from 'common/api/api'
import { retargetingListApi } from 'common/api/entitiesApi'
import { AnchorsForm } from 'common/components/AnchorsForm'
import { FormCard } from 'common/components/FormCard'
import { useAppContext } from 'common/context/AppContext'
import { CommonRoutes } from 'common/context/commonRoutes'
import { retargetingListStatusColors } from 'common/pages/retargeting-list/RetargetingListList'
import { apiCall } from 'common/util/Util'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import StatusEnum = RetargetingListBean.StatusEnum

const InfoValue = ({ children }: { children?: React.ReactNode }) => (
  <div style={{ minHeight: 32, display: 'flex', alignItems: 'center' }}>
    <Typography.Text>{children || '-'}</Typography.Text>
  </div>
)

const RetargetingListForm = () => {
  const { id } = useParams<{ id?: string }>()
  const idNum = id ? parseInt(id, 10) : 0
  const appContext = useAppContext()
  const navigateTo = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [entity, setEntity] = useState<RetargetingListBean>()

  useEffect(() => {
    if (!idNum) {
      navigateTo(CommonRoutes.RETARGETING_LIST)
    }
  }, [idNum, navigateTo])

  useEffect(() => {
    if (!idNum) {
      return
    }

    apiCall(setLoading, appContext, () => retargetingListApi.getOne(idNum), (response) => {
      setEntity(response.data)

    })
  }, [appContext, form, idNum])

  const filters = entity?.filterDisplay || []
  const onCancel = () => navigateTo(CommonRoutes.RETARGETING_LIST)

  const deleteRetargetingList = () => {
    apiCall(
      setLoading,
      appContext,
      () => retargetingListApi._delete(idNum),
      () => navigateTo(CommonRoutes.RETARGETING_LIST),
    )
  }

  return (
    <Spin spinning={loading}>
      <AnchorsForm form={form} onFinish={() => undefined} onCancel={onCancel}>
        <FormCard id="retargetingList" title={entity?.name || 'Retargeting List'}>
          <Form.Item label="Advertiser">
            <InfoValue>{entity?.advertiserName}</InfoValue>
          </Form.Item>

          <Form.Item label="Creation Date">
            <InfoValue>{entity?.created ? dayjs(entity.created).format('DD MMM YYYY') : '-'}</InfoValue>
          </Form.Item>

          <Form.Item label="Period">
            <InfoValue>
              {entity?.startDate && entity?.endDate
                ? `${dayjs(entity.startDate).format('DD MMM YYYY')} - ${dayjs(entity.endDate).format('DD MMM YYYY')}`
                : '-'}
            </InfoValue>
          </Form.Item>

          <Form.Item label="Auto Update">
            <InfoValue>{entity?.autoUpdate ? 'Enabled' : 'Disabled'}</InfoValue>
          </Form.Item>

          {entity?.status && (
            <Form.Item label="Status">
              <Tag color={retargetingListStatusColors[entity.status as StatusEnum]} style={{ color: '#0D1A43' }}>
                {entity.status}
              </Tag>
            </Form.Item>
          )}

          <Form.Item label="Filters">
            {filters.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 6 }}>
                {filters.map((filter) => (
                  <div key={`${filter.title}-${(filter.values || []).join('|')}`}>
                    <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                      {filter.title}
                    </Typography.Text>
                    <Typography.Text>{filter.values?.length ? filter.values.join(', ') : '-'}</Typography.Text>
                  </div>
                ))}
              </div>
            ) : (
              <Typography.Text type="secondary">No filters</Typography.Text>
            )}
          </Form.Item>
        </FormCard>

        <Row>
          <Col span="4" />
          <Col span="20">
            <Flex style={{ marginTop: 20, marginRight: 24, marginLeft: 16 }}>
              <Flex gap="middle" justify="flex-start" align="flex-start">
                <Button type="primary" className="adsp-button" htmlType="button" danger ghost onClick={onCancel}>
                  Cancel
                </Button>
              </Flex>
              <Flex flex="content" justify="end" align="end">
                <Button type="primary" className="adsp-button" danger onClick={() => setDeleteModalOpen(true)}>
                  Delete
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>

        <Modal
          title="Delete the retargeting list"
          open={deleteModalOpen}
          okText="Yes"
          cancelText="Cancel"
          onOk={deleteRetargetingList}
          onCancel={() => setDeleteModalOpen(false)}
        >
          Are you sure?
        </Modal>
      </AnchorsForm>
    </Spin>
  )
}

export default RetargetingListForm
